import React, { useContext, useEffect, useState, useCallback, useRef, useMemo } from "react";
import {
  Box, Typography, Stack, IconButton, Button, Tooltip, TextField,
  CircularProgress, Alert, Chip, Divider,
} from "@mui/material";
import {
  FolderOpen, InsertDriveFile, ArrowBack, Download,
  Close, Refresh, ChevronRight, Home as HomeIcon,
  PictureAsPdf, NavigateNext, CheckCircle, Error as ErrorIcon,
  InfoOutlined, Search as SearchIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import UserContext from "./UserContext";
import ThemeToggle from "./ThemeToggle";
import NavUserMenu from "./NavUserMenu";
import { formatEST } from "../dateUtils";
import { extractErrorDetail } from "./validators";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const REPORTS_ENABLED = process.env.REACT_APP_LAB_REPORTS_ENABLED === "true"; // same flag Home.js checks for the nav card

const BRAND = {
  navy:      "#00357a",
  navyDark:  "#001f4d",
  navyLight: "#5b9bd5",
  gold:      "#c9a96e",
  offWhite:  "#f8f6f2",
};

// ── Utilities ─────────────────────────────────────────────────────────────────
function formatBytes(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function formatDate(iso) {
  return formatEST(iso, { dateOnly: true }); // 2026-10-04 — explicit instruction: every timestamp shows in EST, labeled
}

// 2026-10-21 fix (issue checklist #15) — breadcrumb crumbs here used to be
// the raw GCS folder segment ("proove_opioid_risk", "ACC-1_20261021")
// verbatim, with no semantic meaning to a clinician/patient — "I see
// folders but no reports" with zero indication what each folder actually
// represents. Only the well-known, fixed Proove report-type segment and
// the accession/date segment get a real label here — everything else
// (identity folder, practice/provider folder for clinicians) is left as
// the raw string, since those aren't a small fixed vocabulary this
// component can safely hardcode without drifting from the real,
// lab-configurable test catalog.
const REPORT_TYPE_CRUMB_LABELS = {
  proove_opioid_risk: "Opioid Risk Profile",
  proove_pain_perception: "Pain Perception Profile",
  proove_pgx: "Drug Metabolism (PGx) Profile",
};

function breadcrumbDisplayName(crumb) {
  const label = crumb.replace(/\/$/, "");
  if (REPORT_TYPE_CRUMB_LABELS[label]) return REPORT_TYPE_CRUMB_LABELS[label];
  // Accession/date folder shape: "<accession>_YYYYMMDD" (8 trailing digits).
  const dateMatch = label.match(/^(.+)_(\d{4})(\d{2})(\d{2})$/);
  if (dateMatch) {
    const [, accession, year, month, day] = dateMatch;
    const d = new Date(Number(year), Number(month) - 1, Number(day));
    if (!Number.isNaN(d.getTime())) {
      const formatted = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
      return `${accession} · ${formatted}`;
    }
  }
  // 2026-10-26 addition (user-directed) — the identity folder segment has
  // a FIXED, reliably parseable shape, unlike the practice/provider
  // folder this function's own original comment was worried about:
  // resolve_identity's root_folder_path is always built as
  // "{_gcs_name_norm(name)}_{_normalize_dob(dob)}_{identity_id}" — and
  // _gcs_name_norm (backend, main.py) deliberately preserves hyphens
  // (only replaces non-word/non-hyphen chars), so the DOB segment always
  // comes through as a literal "YYYY-MM-DD", and identity_id always
  // matches /^id[0-9a-f]{12}$/ — giving an unambiguous split point no
  // real person's name could accidentally produce. Falls through to the
  // raw string untouched if the shape doesn't match exactly (never
  // guesses at a malformed or unexpected folder name).
  const identityMatch = label.match(/^(.+)_(\d{4}-\d{2}-\d{2})_(id[0-9a-f]{12})$/);
  if (identityMatch) {
    const [, nameNorm, dob] = identityMatch;
    const displayName = nameNorm.split("_").filter(Boolean).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
    const d = new Date(`${dob}T00:00:00`);
    const formattedDob = Number.isNaN(d.getTime())
      ? dob
      : d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
    return `${displayName} · ${formattedDob}`;
  }
  return label;
}

function parseBreadcrumbs(subPrefix) {
  if (!subPrefix) return [];
  return subPrefix.split("/").filter(Boolean);
}

// ── Toast system ──────────────────────────────────────────────────────────────
function ToastItem({ toast, onRemove }) {
  const DURATION = 4200;
  const [progress, setProgress] = useState(100);
  const intervalRef = useRef(null);

  useEffect(() => {
    const step = 100 / (DURATION / 60);
    intervalRef.current = setInterval(() => {
      setProgress((p) => {
        if (p - step <= 0) { clearInterval(intervalRef.current); onRemove(toast.id); return 0; }
        return p - step;
      });
    }, 60);
    return () => clearInterval(intervalRef.current);
  }, [toast.id, onRemove]);

  const icons = { success: <CheckCircle sx={{ fontSize: 18 }} />, error: <ErrorIcon sx={{ fontSize: 18 }} />, info: <InfoOutlined sx={{ fontSize: 18 }} /> };
  const colors = { success: "#22c55e", error: "#ef4444", info: BRAND.navyLight };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: 60, scale: 0.92 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 60, scale: 0.88 }}
      transition={{ type: "spring", stiffness: 380, damping: 28 }}
      style={{ marginBottom: 10 }}
    >
      <Box sx={{
        minWidth: 280, maxWidth: 360, borderRadius: "12px", overflow: "hidden",
        bgcolor: "rgba(15,23,42,0.95)", backdropFilter: "blur(20px)",
        boxShadow: "0 8px 32px rgba(0,0,0,0.4), 0 2px 8px rgba(0,0,0,0.3)",
        border: "1px solid rgba(255,255,255,0.08)",
      }}>
        <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "flex-start", gap: 1.25 }}>
          <Box sx={{ color: colors[toast.type], mt: 0.1, flexShrink: 0 }}>{icons[toast.type]}</Box>
          <Typography sx={{ fontSize: 15, color: "#f1f5f9", flex: 1, lineHeight: 1.5 }}>
            {toast.message}
          </Typography>
          <IconButton size="small" onClick={() => onRemove(toast.id)}
            sx={{ color: "rgba(255,255,255,0.35)", p: 0.25, mt: -0.25,
              "&:hover": { color: "#fff" } }}>
            <Close sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
        <Box sx={{ height: 3, bgcolor: "rgba(255,255,255,0.06)", position: "relative" }}>
          <motion.div
            initial={{ width: "100%" }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.06, ease: "linear" }}
            style={{ height: "100%", background: colors[toast.type], borderRadius: "0 0 0 0" }}
          />
        </Box>
      </Box>
    </motion.div>
  );
}

function ToastContainer({ toasts, removeToast }) {
  return (
    <Box sx={{ position: "fixed", bottom: 28, right: 24, zIndex: 9999, pointerEvents: "none" }}>
      <AnimatePresence>
        {toasts.map((t) => (
          <Box key={t.id} sx={{ pointerEvents: "auto" }}>
            <ToastItem toast={t} onRemove={removeToast} />
          </Box>
        ))}
      </AnimatePresence>
    </Box>
  );
}

// ── Orbital loader ────────────────────────────────────────────────────────────
function OrbitalLoader() {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      py: 10, gap: 3 }}>
      <Box sx={{ position: "relative", width: 72, height: 72 }}>
        {/* Outer ring */}
        <Box sx={{
          position: "absolute", inset: 0, borderRadius: "50%",
          border: "2px solid rgba(0,53,122,0.15)",
        }} />
        {/* Spinning arc */}
        <Box sx={{
          position: "absolute", inset: 0, borderRadius: "50%",
          border: "2px solid transparent",
          borderTopColor: BRAND.navy,
          "@keyframes spin": { to: { transform: "rotate(360deg)" } },
          animation: "spin 1.1s linear infinite",
        }} />
        {/* Counter-rotating gold arc */}
        <Box sx={{
          position: "absolute", inset: 8, borderRadius: "50%",
          border: "2px solid transparent",
          borderBottomColor: BRAND.gold,
          "@keyframes spinReverse": { to: { transform: "rotate(-360deg)" } },
          animation: "spinReverse 1.6s linear infinite",
        }} />
        {/* Pulsing center orb */}
        <Box sx={{
          position: "absolute", inset: "50%", transform: "translate(-50%,-50%)",
          width: 10, height: 10, borderRadius: "50%",
          background: `radial-gradient(circle, ${BRAND.gold}, ${BRAND.navy})`,
          "@keyframes orb": { "0%,100%": { opacity: 1, transform: "translate(-50%,-50%) scale(1)" }, "50%": { opacity: 0.5, transform: "translate(-50%,-50%) scale(0.6)" } },
          animation: "orb 1.6s ease-in-out infinite",
        }} />
      </Box>
      <Typography sx={{ fontSize: 14.5, color: "rgba(0,53,122,0.55)", fontWeight: 600, letterSpacing: "0.08em" }}>
        LOADING REPORTS…
      </Typography>
    </Box>
  );
}


// ── Folder card ───────────────────────────────────────────────────────────────
function FolderCard({ name, onClick, isDark, border }) {
  // 2026-10-26 fix (confirmed real gap, user-directed) — breadcrumbDisplayName
  // already existed (2026-10-21) and correctly converts the raw report-type
  // segment ("proove_pgx") into a readable label ("Drug Metabolism (PGx)
  // Profile"), but it was only ever wired into the breadcrumb trail above
  // the grid, never into the actual tiles a person clicks on — so the
  // very first thing anyone sees browsing into a folder was still the raw
  // GCS segment. Applying the same function here closes that gap for
  // report-type/accession-date folders; identity/practice folders still
  // show as the raw string (see breadcrumbDisplayName's own comment on
  // why that one's a separate, bigger piece of work).
  const label = breadcrumbDisplayName(name);
  return (
    <motion.div
      whileHover={{ y: -4, scale: 1.02 }}
      whileTap={{ scale: 0.97 }}
      transition={{ type: "spring", stiffness: 380, damping: 24 }}
      style={{ cursor: "pointer" }}
      onClick={onClick}
    >
      <Box sx={{
        p: 2.5, borderRadius: "14px",
        border: `1px solid ${border}`,
        bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
        backdropFilter: "blur(12px)",
        boxShadow: isDark ? "0 2px 12px rgba(0,0,0,0.15)" : "0 2px 12px rgba(0,53,122,0.04)",
        display: "flex", alignItems: "center", gap: 2,
        transition: "box-shadow 0.25s, border-color 0.25s",
        "&:hover": {
          borderColor: BRAND.gold,
          boxShadow: isDark ? "0 8px 28px rgba(0,0,0,0.3)" : "0 8px 28px rgba(0,53,122,0.12)",
        },
      }}>
        <Box sx={{
          width: 48, height: 48, borderRadius: "12px", flexShrink: 0,
          background: `linear-gradient(135deg, ${BRAND.navy} 0%, #1a4b9e 100%)`,
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 4px 12px rgba(0,53,122,0.3)",
        }}>
          <FolderOpen sx={{ color: BRAND.gold, fontSize: 24 }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Tooltip title={label} enterDelay={400}>
            <Typography sx={{
              fontSize: 15, fontWeight: 700,
              color: isDark ? "#f1f5f9" : BRAND.navy,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {label}
            </Typography>
          </Tooltip>
          <Typography sx={{ fontSize: 12.5, color: isDark ? "#64748b" : "#94a3b8", mt: 0.3, fontWeight: 500 }}>
            Folder
          </Typography>
        </Box>
        <ChevronRight sx={{ color: BRAND.gold, ml: "auto", flexShrink: 0, fontSize: 20 }} />
      </Box>
    </motion.div>
  );
}

// ── File card ─────────────────────────────────────────────────────────────────
function FileCard({ file, onPreview, onDownload, isDark, border }) {
  const [downloading, setDownloading] = useState(false);
  return (
    <motion.div
      whileHover={{ y: -4, scale: 1.02 }}
      transition={{ type: "spring", stiffness: 380, damping: 24 }}
    >
      <Box sx={{
        p: 2.5, borderRadius: "14px",
        border: `1px solid ${border}`,
        bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
        backdropFilter: "blur(12px)",
        boxShadow: isDark ? "0 2px 12px rgba(0,0,0,0.15)" : "0 2px 12px rgba(0,53,122,0.04)",
        transition: "box-shadow 0.25s, border-color 0.25s",
        "&:hover": {
          borderColor: BRAND.gold,
          boxShadow: isDark ? "0 8px 28px rgba(0,0,0,0.3)" : "0 8px 28px rgba(0,53,122,0.12)",
        },
      }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 2.5 }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: "10px", flexShrink: 0,
            background: "linear-gradient(135deg, #c8322a 0%, #ef4444 100%)",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 4px 12px rgba(239,68,68,0.3)",
          }}>
            <PictureAsPdf sx={{ color: "#fff", fontSize: 22 }} />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Tooltip title={file.filename} enterDelay={400}>
              <Typography sx={{
                fontSize: 14.5, fontWeight: 700,
                color: isDark ? "#f1f5f9" : "#1e293b",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                lineHeight: 1.4,
              }}>
                {file.filename}
              </Typography>
            </Tooltip>
            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
              <Typography sx={{ fontSize: 12.5, color: isDark ? "#64748b" : "#94a3b8" }}>
                {formatBytes(file.size_bytes)}
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: isDark ? "#475569" : "#cbd5e1" }}>·</Typography>
              <Typography sx={{ fontSize: 12.5, color: isDark ? "#64748b" : "#94a3b8" }}>
                {formatDate(file.updated)}
              </Typography>
            </Stack>
          </Box>
        </Box>

        <Stack direction="row" spacing={1.5}>
          <Button size="small" variant="outlined" fullWidth onClick={() => onPreview(file)}
            sx={{
              fontSize: 13, fontWeight: 700, borderRadius: "8px", py: 0.75,
              textTransform: "none", letterSpacing: "0.02em",
              borderColor: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,53,122,0.15)",
              color: isDark ? "#94a3b8" : "#475569",
              "&:hover": { borderColor: BRAND.navy, color: BRAND.navy, bgcolor: "rgba(0,53,122,0.04)" },
            }}>
            Preview
          </Button>
          <Button size="small" variant="contained" fullWidth
            disabled={downloading}
            startIcon={downloading
              ? <CircularProgress size={12} sx={{ color: "#fff" }} />
              : <Download sx={{ fontSize: 14 }} />}
            onClick={async () => {
              setDownloading(true);
              await onDownload(file);
              setDownloading(false);
            }}
            sx={{
              fontSize: 13, fontWeight: 700, borderRadius: "8px", py: 0.75,
              textTransform: "none", letterSpacing: "0.02em",
              background: `linear-gradient(135deg, ${BRAND.navy} 0%, #004299 100%)`,
              boxShadow: "0 3px 10px rgba(0,53,122,0.3)",
              "&:hover": { boxShadow: "0 4px 14px rgba(0,53,122,0.45)" },
              "&:disabled": { opacity: 0.6 },
            }}>
            {downloading ? "…" : "Download"}
          </Button>
        </Stack>
      </Box>
    </motion.div>
  );
}

// ── PDF Preview panel ─────────────────────────────────────────────────────────
function PreviewPanel({ file, signedUrl, onClose, isDark, border }) {
  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        style={{
          position: "fixed", inset: 0, zIndex: 1199,
          background: "rgba(0,0,0,0.45)", backdropFilter: "blur(4px)",
        }}
      />
      <motion.div
        initial={{ x: "100%", opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: "100%", opacity: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 32 }}
        style={{
          position: "fixed", top: 0, right: 0, bottom: 0,
          width: "min(700px, 100vw)", zIndex: 1200,
          display: "flex", flexDirection: "column",
          boxShadow: "-12px 0 60px rgba(0,0,0,0.35)",
        }}
      >
        <Box sx={{
          height: "100%", display: "flex", flexDirection: "column",
          bgcolor: isDark ? "#111827" : "#ffffff",
          borderLeft: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,53,122,0.12)"}`,
        }}>
          {/* Top stripe */}
          <Box sx={{ height: 3, background: `linear-gradient(to right, ${BRAND.navy}, ${BRAND.gold})`, flexShrink: 0 }} />

          {/* Preview header */}
          <Box sx={{
            px: 3, py: 2.5, flexShrink: 0,
            borderBottom: `1px solid ${border}`,
            display: "flex", alignItems: "center", justifyContent: "space-between",
            bgcolor: isDark ? "rgba(0,53,122,0.18)" : "rgba(0,53,122,0.03)",
          }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0, flex: 1 }}>
              <Box sx={{
                width: 36, height: 36, borderRadius: "8px", flexShrink: 0,
                background: "linear-gradient(135deg, #c8322a, #ef4444)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <PictureAsPdf sx={{ color: "#fff", fontSize: 18 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Tooltip title={file.filename} enterDelay={400}>
                  <Typography sx={{
                    fontSize: 15, fontWeight: 700,
                    color: isDark ? "#f1f5f9" : BRAND.navy,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>
                    {file.filename}
                  </Typography>
                </Tooltip>
                <Typography sx={{ fontSize: 12.5, color: isDark ? "#64748b" : "#94a3b8" }}>
                  {formatBytes(file.size_bytes)} · {formatDate(file.updated)}
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={0.5}>
              {signedUrl && (
                <Tooltip title="Download">
                  <IconButton size="small" component="a" href={signedUrl} download target="_blank"
                    sx={{
                      color: isDark ? "#94a3b8" : "#64748b", borderRadius: "8px", p: 0.85,
                      "&:hover": { color: BRAND.navy, bgcolor: "rgba(0,53,122,0.1)" },
                    }}>
                    <Download sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Close preview">
                <IconButton size="small" onClick={onClose}
                  sx={{
                    color: isDark ? "#94a3b8" : "#64748b", borderRadius: "8px", p: 0.85,
                    "&:hover": { color: "#ef4444", bgcolor: "rgba(239,68,68,0.1)" },
                  }}>
                  <Close sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>

          {/* Preview content */}
          <Box sx={{ flex: 1, overflow: "hidden", position: "relative" }}>
            {signedUrl ? (
              <iframe
                src={signedUrl}
                title={file.filename}
                style={{ width: "100%", height: "100%", border: "none" }}
              />
            ) : (
              <Box sx={{
                display: "flex", alignItems: "center", justifyContent: "center",
                height: "100%", flexDirection: "column", gap: 2,
              }}>
                <OrbitalLoader />
              </Box>
            )}
          </Box>
        </Box>
      </motion.div>
    </>
  );
}

// ── Main LabReports component ─────────────────────────────────────────────────
export default function LabReports() {
  const theme   = useTheme();
  const isDark  = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  const bg      = isDark ? "#080d1a" : "#f4f7ff";
  const sidebar = isDark ? "rgba(11,17,32,0.92)" : "rgba(255,255,255,0.92)";
  const border  = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,53,122,0.1)";
  const textSub = isDark ? "#94a3b8" : "#64748b";

  const [subPrefix,   setSubPrefix]   = useState("");
  const [data,        setData]        = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState("");
  const [previewFile, setPreviewFile] = useState(null);
  const [signedUrl,   setSignedUrl]   = useState("");
  const [toasts,      setToasts]      = useState([]);
  // 2026-10-21 fix (issue checklist #62-adjacent plan item) — this view had
  // no search at all; a clinician with hundreds/thousands of patient
  // folders had no way to jump to one directly. Client-side filter over the
  // CURRENT folder level only — this component is already scoped to one
  // user's own GCS prefix (_get_user_gcs_prefix), so the result set at any
  // one level is small enough that no new backend route is needed.
  const [searchQuery, setSearchQuery] = useState("");

  const token = user?.token;

  const addToast = useCallback((message, type = "info") => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, message, type }]);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const browse = useCallback(async (sp = "") => {
    setLoading(true);
    setError("");
    setSearchQuery(""); // a stale filter from the previous folder would be confusing here
    try {
      const res = await axios.get(`${API_BASE}/my-reports/browse`, {
        headers: { Authorization: `Bearer ${token}` },
        params:  { sub_prefix: sp },
      });
      setData(res.data);
      setSubPrefix(sp);
    } catch (err) {
      const msg = extractErrorDetail(err, "Failed to load reports. Please try again.");
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    // Fixed 2026-10-08 — same class of bug as Register.js's consent-text
    // fetch: this route carries no feature-flag check of its own (Home.js
    // only hides the nav card behind REACT_APP_LAB_REPORTS_ENABLED), so
    // reaching /my-reports via direct URL while the flag is off still
    // fired this GCS-browse call unconditionally. Skip it when disabled.
    if (!REPORTS_ENABLED) {
      setLoading(false);
      setError("Lab report browsing is not available right now.");
      return;
    }
    browse("");
  }, [browse]);

  const openFolder = (folderRelative) => {
    const sp = subPrefix + folderRelative + (folderRelative.endsWith("/") ? "" : "/");
    browse(sp);
    setPreviewFile(null);
    setSignedUrl("");
  };

  const goUp = () => {
    const parts = subPrefix.split("/").filter(Boolean);
    parts.pop();
    const up = parts.length ? parts.join("/") + "/" : "";
    browse(up);
    setPreviewFile(null);
    setSignedUrl("");
  };

  const getSignedUrl = async (file) => {
    try {
      const res = await axios.get(`${API_BASE}/my-reports/signed-url`, {
        headers: { Authorization: `Bearer ${token}` },
        params:  { blob_name: file.blob_name },
      });
      return res.data.url;
    } catch {
      addToast("Could not generate download link. Please try again.", "error");
      return "";
    }
  };

  const handlePreview = async (file) => {
    setPreviewFile(file);
    setSignedUrl("");
    const url = await getSignedUrl(file);
    setSignedUrl(url);
  };

  const handleDownload = async (file) => {
    const url = await getSignedUrl(file);
    if (url) {
      const a = document.createElement("a");
      a.href = url;
      a.download = file.filename;
      a.click();
      addToast(`Downloading ${file.filename}`, "success");
    }
  };

  const breadcrumbs = parseBreadcrumbs(subPrefix);
  const isClinician = user?.role === "clinician";
  const hasContent  = data && (data.folders.length > 0 || data.files.length > 0);

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const filteredFolders = useMemo(() => {
    if (!data) return [];
    if (!normalizedQuery) return data.folders;
    return data.folders.filter((folder) => folder.toLowerCase().includes(normalizedQuery));
  }, [data, normalizedQuery]);
  const filteredFiles = useMemo(() => {
    if (!data) return [];
    if (!normalizedQuery) return data.files;
    return data.files.filter((file) => file.filename.toLowerCase().includes(normalizedQuery));
  }, [data, normalizedQuery]);
  const hasFilteredContent = filteredFolders.length > 0 || filteredFiles.length > 0;
  const searchActiveWithNoMatches = hasContent && normalizedQuery && !hasFilteredContent;

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column", bgcolor: bg, overflow: "hidden", position: "relative" }}>

      {/* Animated background blobs */}
      <Box sx={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0 }}>
        {[
          { top: "-8%",  left: "-4%",  size: 500, color: isDark ? "rgba(0,53,122,0.14)" : "rgba(0,53,122,0.06)", delay: 0 },
          { top: "55%",  right: "-6%", size: 420, color: isDark ? "rgba(192,168,140,0.06)" : "rgba(192,168,140,0.1)", delay: 1.5 },
          { top: "25%",  left: "48%",  size: 320, color: isDark ? "rgba(0,53,122,0.08)" : "rgba(0,53,122,0.04)", delay: 0.8 },
        ].map((b, i) => (
          <motion.div key={i}
            style={{ position: "absolute", top: b.top, left: b.left, right: b.right,
              width: b.size, height: b.size, borderRadius: "50%",
              background: b.color, filter: "blur(80px)" }}
            animate={{ scale: [1, 1.1, 1], opacity: [0.8, 1, 0.8] }}
            transition={{ duration: 9 + i * 2.5, repeat: 2, delay: b.delay, ease: "easeInOut" }}
          />
        ))}
      </Box>

      {/* ── Header ── */}
      <motion.div initial={{ y: -64, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, ease: "easeOut" }} style={{ position: "relative", zIndex: 10, flexShrink: 0 }}>
        <Box sx={{
          bgcolor: isDark ? "rgba(2,6,15,0.92)" : "rgba(0,31,77,0.97)",
          backdropFilter: "blur(16px) saturate(1.4)",
          borderBottom: `2px solid rgba(192,168,140,0.3)`,
          boxShadow: "0 4px 24px rgba(0,0,0,0.2)",
        }}>
          <Box sx={{ px: 3, py: 1, minHeight: 64, display: "flex", alignItems: "center", flexWrap: "wrap", rowGap: 1,
            justifyContent: "space-between", maxWidth: 1440, mx: "auto", width: "100%" }}>

            <Box component="a" href="https://labarium.com/" target="_blank" rel="noopener noreferrer"
              sx={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
              <img src="/logo-labarium-dark.png" alt="Labarium" style={{ height: 38, width: "auto" }} />
            </Box>

            <Stack direction="row" spacing={1.5} alignItems="center">
              {/* Live badge */}
              <Box sx={{
                display: { xs: "none", sm: "flex" }, alignItems: "center", gap: 0.75,
                bgcolor: "rgba(192,168,140,0.12)", border: "1px solid rgba(192,168,140,0.28)",
                borderRadius: "8px", px: 1.5, py: 0.5,
              }}>
                <Box sx={{
                  width: 6, height: 6, borderRadius: "50%", bgcolor: BRAND.gold,
                  "@keyframes pulse": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.3 } },
                  animation: "pulse 2s infinite",
                }} />
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: BRAND.gold, letterSpacing: "0.1em" }}>
                  MY REPORTS
                </Typography>
              </Box>

              <ThemeToggle />

              <Box onClick={() => navigate("/home")}
                sx={{
                  display: "flex", alignItems: "center", gap: 0.75,
                  color: "rgba(255,255,255,0.6)", cursor: "pointer",
                  fontSize: 13.5, fontWeight: 600, px: 1.25, py: 0.5,
                  borderRadius: "8px", transition: "color 0.2s, background 0.2s",
                  "&:hover": { color: BRAND.gold, bgcolor: "rgba(192,168,140,0.1)" },
                }}>
                <ArrowBack sx={{ fontSize: 15 }} />
                <Box sx={{ display: { xs: "none", sm: "block" } }}>Home</Box>
              </Box>

              <NavUserMenu />
            </Stack>
          </Box>
        </Box>
      </motion.div>

      {/* ── Body ── */}
      <Box sx={{ flex: 1, display: "flex", overflow: "hidden", maxWidth: 1440, mx: "auto", width: "100%", position: "relative", zIndex: 10 }}>

        {/* ── Left sidebar ── */}
        <motion.div
          initial={{ x: -40, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.15, ease: "easeOut" }}
          style={{ display: "flex", flexShrink: 0 }}
        >
          <Box sx={{
            width: 268, flexShrink: 0,
            bgcolor: sidebar,
            backdropFilter: "blur(24px) saturate(1.4)",
            borderRight: `1px solid ${border}`,
            display: { xs: "none", md: "flex" },
            flexDirection: "column", p: 3,
            boxShadow: isDark ? "4px 0 20px rgba(0,0,0,0.2)" : "4px 0 20px rgba(0,53,122,0.04)",
          }}>
            {/* Section header */}
            <Typography sx={{
              fontSize: 11.5, fontWeight: 800, letterSpacing: "0.18em",
              color: textSub, mb: 2.5,
            }}>STORAGE</Typography>

            {/* Archive info card */}
            <Box sx={{
              p: 2, borderRadius: "12px",
              background: isDark
                ? "linear-gradient(135deg, rgba(0,53,122,0.3), rgba(0,53,122,0.15))"
                : "linear-gradient(135deg, rgba(0,53,122,0.08), rgba(0,53,122,0.04))",
              border: `1px solid ${isDark ? "rgba(0,53,122,0.5)" : "rgba(0,53,122,0.12)"}`,
              mb: 2.5,
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
                <Box sx={{
                  width: 28, height: 28, borderRadius: "8px",
                  background: `linear-gradient(135deg, ${BRAND.navy}, #1a4b9e)`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <FolderOpen sx={{ color: BRAND.gold, fontSize: 15 }} />
                </Box>
                <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: isDark ? BRAND.gold : BRAND.navy }}>
                  {isClinician ? "Clinician Archive" : "Patient Archive"}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 13, color: textSub, lineHeight: 1.6 }}>
                {isClinician
                  ? `Reports for ${user?.practice_name || "your practice"}`
                  : `Reports for ${user?.name || "you"}`}
              </Typography>
            </Box>

            <Divider sx={{ mb: 2.5, borderColor: border }} />

            <Typography sx={{
              fontSize: 11.5, fontWeight: 800, letterSpacing: "0.14em",
              color: textSub, mb: 1.5,
            }}>QUICK NAV</Typography>

            {/* All Reports nav item */}
            <Box onClick={() => browse("")}
              sx={{
                display: "flex", alignItems: "center", gap: 1.25,
                px: 1.5, py: 1, borderRadius: "10px", cursor: "pointer",
                bgcolor: !subPrefix
                  ? (isDark ? "rgba(0,53,122,0.3)" : "rgba(0,53,122,0.1)")
                  : "transparent",
                border: !subPrefix
                  ? `1px solid ${isDark ? "rgba(0,53,122,0.5)" : "rgba(0,53,122,0.2)"}`
                  : "1px solid transparent",
                "&:hover": { bgcolor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,53,122,0.06)" },
                transition: "background 0.18s, border-color 0.18s",
              }}>
              <HomeIcon sx={{ fontSize: 16, color: !subPrefix ? BRAND.navy : textSub }} />
              <Typography sx={{
                fontSize: 14.5, fontWeight: !subPrefix ? 700 : 500,
                color: !subPrefix ? (isDark ? "#f1f5f9" : BRAND.navy) : textSub,
              }}>
                All Reports
              </Typography>
              {!subPrefix && (
                <Box sx={{
                  ml: "auto", width: 6, height: 6, borderRadius: "50%",
                  bgcolor: BRAND.gold,
                }} />
              )}
            </Box>

            {/* Breadcrumb nav items */}
            {breadcrumbs.map((crumb, i) => {
              const sp = breadcrumbs.slice(0, i + 1).join("/") + "/";
              const active = sp === subPrefix;
              return (
                <Box key={sp} onClick={() => browse(sp)}
                  sx={{
                    display: "flex", alignItems: "center", gap: 1.25,
                    pl: 1.5 + (i + 1) * 1.5, pr: 1.5, py: 1,
                    borderRadius: "10px", cursor: "pointer",
                    bgcolor: active
                      ? (isDark ? "rgba(0,53,122,0.3)" : "rgba(0,53,122,0.1)")
                      : "transparent",
                    border: active
                      ? `1px solid ${isDark ? "rgba(0,53,122,0.5)" : "rgba(0,53,122,0.2)"}`
                      : "1px solid transparent",
                    "&:hover": { bgcolor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,53,122,0.05)" },
                    transition: "background 0.18s",
                  }}>
                  <FolderOpen sx={{ fontSize: 14, color: active ? BRAND.navy : textSub, flexShrink: 0 }} />
                  <Tooltip title={crumb} enterDelay={400}>
                    <Typography sx={{
                      fontSize: 13.5, fontWeight: active ? 700 : 400,
                      color: active ? (isDark ? "#f1f5f9" : BRAND.navy) : textSub,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      minWidth: 0,
                    }}>
                      {breadcrumbDisplayName(crumb)}
                    </Typography>
                  </Tooltip>
                </Box>
              );
            })}

            {/* Spacer + disclaimer */}
            <Box sx={{ flex: 1 }} />
            <Box sx={{
              mt: 3, p: 1.75, borderRadius: "10px",
              bgcolor: isDark ? "rgba(192,168,140,0.06)" : "rgba(192,168,140,0.08)",
              border: `1px solid rgba(192,168,140,0.18)`,
            }}>
              <Typography sx={{ fontSize: 12, color: isDark ? "rgba(192,168,140,0.7)" : "#78716c", lineHeight: 1.65 }}>
                Reports are end-to-end encrypted and accessible only to you.
              </Typography>
            </Box>
          </Box>
        </motion.div>

        {/* ── Main content ── */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>

          {/* Toolbar / breadcrumb bar */}
          <motion.div
            initial={{ y: -16, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.2 }}
          >
            <Box sx={{
              px: { xs: 2, md: 4 }, py: 2,
              borderBottom: `1px solid ${border}`,
              display: "flex", alignItems: "center", justifyContent: "space-between",
              flexWrap: "wrap", gap: 1, flexShrink: 0,
              bgcolor: isDark ? "rgba(255,255,255,0.015)" : "rgba(255,255,255,0.6)",
              backdropFilter: "blur(12px)",
            }}>
              {/* Breadcrumb */}
              <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap">
                <Box onClick={() => browse("")}
                  sx={{ display: "flex", alignItems: "center", cursor: "pointer",
                    color: BRAND.navy, "&:hover": { color: BRAND.gold }, transition: "color 0.18s" }}>
                  <HomeIcon sx={{ fontSize: 18 }} />
                </Box>
                {breadcrumbs.map((crumb, i) => {
                  const sp = breadcrumbs.slice(0, i + 1).join("/") + "/";
                  const isLast = i === breadcrumbs.length - 1;
                  return (
                    <React.Fragment key={sp}>
                      <NavigateNext sx={{ fontSize: 16, color: isDark ? "#475569" : "#cbd5e1" }} />
                      <Tooltip title={crumb} enterDelay={400}>
                        <Typography
                          onClick={() => !isLast && browse(sp)}
                          sx={{
                            fontSize: 14.5, fontWeight: isLast ? 700 : 500,
                            color: isLast ? (isDark ? "#f1f5f9" : BRAND.navy) : textSub,
                            cursor: isLast ? "default" : "pointer",
                            "&:hover": isLast ? {} : { color: BRAND.gold },
                            transition: "color 0.18s",
                          }}>
                          {breadcrumbDisplayName(crumb)}
                        </Typography>
                      </Tooltip>
                    </React.Fragment>
                  );
                })}
              </Stack>

              {/* Actions */}
              <Stack direction="row" spacing={1} alignItems="center">
                {subPrefix && (
                  <Button size="small" startIcon={<ArrowBack sx={{ fontSize: 14.5 }} />}
                    onClick={goUp}
                    sx={{
                      fontSize: 13.5, fontWeight: 600, color: textSub, textTransform: "none",
                      borderRadius: "8px", px: 1.5,
                      "&:hover": { color: BRAND.navy, bgcolor: "rgba(0,53,122,0.06)" },
                    }}>
                    Up
                  </Button>
                )}
                <Tooltip title="Refresh">
                  <IconButton size="small" onClick={() => browse(subPrefix)} disabled={loading}
                    sx={{
                      color: textSub, p: 0.75, borderRadius: "8px",
                      "&:hover": { color: BRAND.navy, bgcolor: "rgba(0,53,122,0.08)" },
                    }}>
                    <Refresh sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
                {hasContent && (
                  <TextField
                    size="small"
                    placeholder="Search this folder…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: <SearchIcon sx={{ fontSize: 17, color: textSub, mr: 0.75 }} />,
                      endAdornment: searchQuery ? (
                        <IconButton size="small" onClick={() => setSearchQuery("")} sx={{ p: 0.4 }}>
                          <Close sx={{ fontSize: 15, color: textSub }} />
                        </IconButton>
                      ) : null,
                    }}
                    sx={{
                      width: 220,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px", fontSize: 13.5, height: 34,
                        bgcolor: isDark ? "rgba(255,255,255,0.04)" : "#fff",
                        "& fieldset": { borderColor: border },
                        "&:hover fieldset": { borderColor: BRAND.navyLight },
                        "&.Mui-focused fieldset": { borderColor: BRAND.navy },
                      },
                    }}
                  />
                )}
                {data && (
                  <Chip
                    label={`${data.folders.length} folder${data.folders.length !== 1 ? "s" : ""}  ·  ${data.files.length} file${data.files.length !== 1 ? "s" : ""}`}
                    size="small"
                    sx={{
                      fontSize: 12.5, height: 24,
                      bgcolor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,53,122,0.05)",
                      border: `1px solid ${border}`, color: textSub,
                      fontWeight: 600,
                    }}
                  />
                )}
              </Stack>
            </Box>
          </motion.div>

          {/* Content area */}
          <Box sx={{ flex: 1, overflowY: "auto", px: { xs: 2, md: 4 }, py: 3 }}>
            {error && (
              <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}>
                <Alert severity="error" sx={{ mb: 3, borderRadius: "12px", fontSize: 14.5 }}
                  onClose={() => setError("")}>
                  {error}
                </Alert>
              </motion.div>
            )}

            {/* 2026-10-26 fix (confirmed real bug, real browser repro) —
                mode="wait" makes AnimatePresence hold the PREVIOUS child
                mounted until its exit animation fully resolves before
                mounting the next one. Confirmed via real debugging that
                `loading`/`data` state were correctly updating on every
                folder navigation, yet the content view stayed stuck on
                the loading spinner forever — a known mode="wait" failure
                mode when the exiting child's animation doesn't cleanly
                resolve (here, the content branch below is keyed by
                `subPrefix`, which changes on every navigation, giving
                AnimatePresence a fresh exit/enter pair to track on every
                click). Dropping mode="wait" (default "sync" behavior,
                enter/exit run together rather than sequenced) removes
                the "stuck waiting on a previous exit" failure mode
                entirely — the only visual cost is the old and new
                content briefly overlapping during the transition instead
                of strictly sequencing, not a functional change. */}
            <AnimatePresence>
              {loading ? (
                <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <OrbitalLoader />
                </motion.div>
              ) : !hasContent ? (
                <motion.div key="empty" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}>
                  <Box sx={{
                    display: "flex", flexDirection: "column", alignItems: "center",
                    justifyContent: "center", py: 12, textAlign: "center",
                  }}>
                    <motion.div
                      animate={{ y: [0, -8, 0] }}
                      transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                    >
                      <Box sx={{
                        width: 88, height: 88, borderRadius: "50%", mb: 3,
                        background: isDark
                          ? "radial-gradient(circle, rgba(0,53,122,0.2), rgba(0,53,122,0.05))"
                          : "radial-gradient(circle, rgba(0,53,122,0.08), rgba(0,53,122,0.02))",
                        border: `2px solid ${isDark ? "rgba(0,53,122,0.35)" : "rgba(0,53,122,0.12)"}`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        boxShadow: isDark
                          ? "0 0 40px rgba(0,53,122,0.2)"
                          : "0 0 40px rgba(0,53,122,0.06)",
                      }}>
                        <InsertDriveFile sx={{ fontSize: 38, color: isDark ? "#334155" : "#cbd5e1" }} />
                      </Box>
                    </motion.div>

                    <Typography sx={{ fontSize: 20, fontWeight: 800,
                      color: isDark ? "#94a3b8" : "#475569", mb: 1.5 }}>
                      No reports here yet
                    </Typography>
                    <Typography sx={{ fontSize: 15, color: textSub, maxWidth: 400, lineHeight: 1.8 }}>
                      {subPrefix
                        ? "This folder is empty."
                        : "Reports processed through Labarium AI will appear here once uploaded to your archive."}
                    </Typography>
                    {subPrefix && (
                      <Button startIcon={<ArrowBack />} onClick={goUp}
                        sx={{
                          mt: 3, textTransform: "none", fontWeight: 700, borderRadius: "10px",
                          color: BRAND.navy, px: 3, py: 1,
                          bgcolor: isDark ? "rgba(0,53,122,0.15)" : "rgba(0,53,122,0.07)",
                          "&:hover": { bgcolor: isDark ? "rgba(0,53,122,0.25)" : "rgba(0,53,122,0.12)" },
                        }}>
                        Back
                      </Button>
                    )}
                  </Box>
                </motion.div>
              ) : searchActiveWithNoMatches ? (
                <motion.div key="no-matches" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}>
                  <Box sx={{
                    display: "flex", flexDirection: "column", alignItems: "center",
                    justifyContent: "center", py: 12, textAlign: "center",
                  }}>
                    <SearchIcon sx={{ fontSize: 48, color: isDark ? "#334155" : "#cbd5e1", mb: 2 }} />
                    <Typography sx={{ fontSize: 18, fontWeight: 800,
                      color: isDark ? "#94a3b8" : "#475569", mb: 1 }}>
                      No matches for "{searchQuery}"
                    </Typography>
                    <Typography sx={{ fontSize: 14.5, color: textSub, maxWidth: 380, lineHeight: 1.7, mb: 2 }}>
                      Nothing in this folder matches that search. Try a different term, or clear it to see everything here.
                    </Typography>
                    <Button onClick={() => setSearchQuery("")}
                      sx={{
                        textTransform: "none", fontWeight: 700, borderRadius: "10px",
                        color: BRAND.navy, px: 3, py: 1,
                        bgcolor: isDark ? "rgba(0,53,122,0.15)" : "rgba(0,53,122,0.07)",
                        "&:hover": { bgcolor: isDark ? "rgba(0,53,122,0.25)" : "rgba(0,53,122,0.12)" },
                      }}>
                      Clear search
                    </Button>
                  </Box>
                </motion.div>
              ) : (
                <motion.div key={subPrefix} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35 }}>

                  {/* Folders section */}
                  {filteredFolders.length > 0 && (
                    <Box sx={{ mb: filteredFiles.length > 0 ? 5 : 0 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
                        <Typography sx={{
                          fontSize: 12, fontWeight: 800, letterSpacing: "0.16em", color: textSub,
                        }}>FOLDERS</Typography>
                        <Box sx={{ flex: 1, height: 1, bgcolor: border }} />
                        <Chip label={filteredFolders.length} size="small" sx={{
                          height: 20, fontSize: 12, fontWeight: 700,
                          bgcolor: isDark ? "rgba(0,53,122,0.2)" : "rgba(0,53,122,0.08)",
                          border: `1px solid ${isDark ? "rgba(0,53,122,0.4)" : "rgba(0,53,122,0.15)"}`,
                          color: isDark ? BRAND.gold : BRAND.navy,
                        }} />
                      </Box>
                      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(248px, 1fr))", gap: 2 }}>
                        {filteredFolders.map((folder, i) => (
                          <motion.div key={folder} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.04 }}>
                            <FolderCard name={folder} isDark={isDark} border={border}
                              onClick={() => openFolder(folder)} />
                          </motion.div>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Files section */}
                  {filteredFiles.length > 0 && (
                    <Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
                        <Typography sx={{
                          fontSize: 12, fontWeight: 800, letterSpacing: "0.16em", color: textSub,
                        }}>FILES</Typography>
                        <Box sx={{ flex: 1, height: 1, bgcolor: border }} />
                        <Chip label={filteredFiles.length} size="small" sx={{
                          height: 20, fontSize: 12, fontWeight: 700,
                          bgcolor: isDark ? "rgba(192,168,140,0.1)" : "rgba(192,168,140,0.12)",
                          border: `1px solid rgba(192,168,140,0.25)`,
                          color: BRAND.gold,
                        }} />
                      </Box>
                      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(248px, 1fr))", gap: 2 }}>
                        {filteredFiles.map((file, i) => (
                          <motion.div key={file.blob_name} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.04 }}>
                            <FileCard file={file} isDark={isDark} border={border}
                              onPreview={handlePreview} onDownload={handleDownload} />
                          </motion.div>
                        ))}
                      </Box>
                    </Box>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </Box>
        </Box>

        {/* ── PDF Preview side panel ── */}
        <AnimatePresence>
          {previewFile && (
            <PreviewPanel
              file={previewFile}
              signedUrl={signedUrl}
              onClose={() => { setPreviewFile(null); setSignedUrl(""); }}
              isDark={isDark}
              border={border}
            />
          )}
        </AnimatePresence>
      </Box>

      {/* ── Toast notifications ── */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </Box>
  );
}
